import React, { Fragment, useRef, useState } from 'react'
import Card from '../../../../../components/atoms/Card'
import Container from '../../../../../components/atoms/Container'
import Heading from '../../../../../components/atoms/Heading'
import Button from '../../../../../components/molecules/Button'
import { useTranslation } from '../../../../../providers/I18n'
import { BankCreditDetail } from '../../../../../types/credits'
import { StyledDescription } from '../../../../../components/styled/Description'
import { useQuery } from 'react-query'
import { useParams } from 'react-router-dom'
import {
  addCreditDocument,
  addCreditHistory,
  getCredit,
  updateCredit,
} from '../../../../../services/credits'
import { getCreditColor } from '../../../../../utils/credits'
import { useUser } from '../../../../../providers/User'
import CreditWaitAction from '../../../shared/CreditWaitAction'
import Textarea from '../../../../../components/atoms/Textarea'
import Spinner from '../../../../../components/atoms/Spinner'
import { CreditPhases, CreditSteps, Role } from '../../../../../types/enums'
import FileDropper, {
  FileWithBuffer,
} from '../../../../../components/molecules/FileDropper'
import { uploadDocuments } from '../../../../../utils/documents'
import { useAlert } from '../../../../../providers/Alert'

const Phase1Analysis: React.FC = () => {
  const commentRef = useRef<HTMLTextAreaElement>(null)

  const { code } = useParams()
  const { user } = useUser()
  const { setAlert } = useAlert()
  const { getTranslation } = useTranslation()
  const [loading, setLoading] = useState<boolean>(false)
  const [analysisResultFile, setAnalysisResultFile] = useState<
    FileWithBuffer[]
  >([])

  const {
    data: credit,
    isLoading,
    refetch,
  } = useQuery<BankCreditDetail>(['getCredit', code], () => getCredit(code!), {
    refetchOnWindowFocus: false,
  })

  let header
  let footer
  let content
  if (!credit && isLoading) {
    content = (
      <Container>
        <StyledDescription>Cargando credito...</StyledDescription>
      </Container>
    )
  } else if (credit) {
    const isActiveOwner = user!.code === credit.owners.phase1Operator?.code
    const color = getCreditColor(credit.phase)
    const creditPhaseStep = `${getTranslation!(
      `phases.${credit.phase}.title`
    )} - ${getTranslation!(`phases.${credit.phase}.steps.${credit.step}`)}`

    header = (
      <Container padding>
        <Heading type="3" color={color}>
          {creditPhaseStep}
        </Heading>
      </Container>
    )

    if (isActiveOwner || user?.role !== Role.Advisor) {
      const analysisResultBuffer = analysisResultFile
        ? analysisResultFile.map((fileWithBuffer) => {
            fileWithBuffer.fixedName = 'analysisResult'
            return fileWithBuffer.file
          })
        : []

      const handleSendToInconsistency = async () => {
        setLoading(true)

        const hasComment = !!commentRef.current?.value
        if (!hasComment) {
          setAlert!({
            severity: 'error',
            title: 'Error',
            description: 'Hacen falta campos requeridos',
          })
          setLoading(false)
        } else {
          await updateCredit(credit!.code, {
            step: CreditSteps.Phase1Inconsistency,
          }).then(async () => {
            const documents: FileWithBuffer[] = []
            if (analysisResultBuffer.length > 0) {
              documents.push(...analysisResultFile)
            }
            const pushedCreditDocuments = await uploadDocuments(
              documents,
              `credit/${credit!.code.toString()}`
            )

            pushedCreditDocuments.forEach((document) => {
              addCreditDocument(credit.code, document!)
            })

            const comment = `Inconsistencia en Credito :: ${commentRef.current?.value}`
            const creditHistoryComment = {
              documents: pushedCreditDocuments,
              comment: comment,
              type: 'status',
              start: new Date(),
              change: {
                from: {
                  owner: credit.owners.phase1Operator._id,
                  phase: credit.phase,
                  step: credit.step,
                },
                to: {
                  owner: credit.owners.advisor._id,
                  phase: CreditPhases.Phase1,
                  step: CreditSteps.Phase1Inconsistency,
                },
              },
            }

            await addCreditHistory(credit!.code, creditHistoryComment).then(
              () => {
                if (commentRef.current) {
                  commentRef.current.value = ''
                }
                refetch().then(() => {
                  setLoading(false)
                })
              }
            )
          })
        }
      }

      const handleSendToConditional = async () => {
        setLoading(true)
        const hasComment = !!commentRef.current?.value
        const hasAnalysisResultFile = analysisResultBuffer.length > 0
        if (!hasComment || !hasAnalysisResultFile) {
          setAlert!({
            severity: 'error',
            title: 'Error',
            description: 'Hacen falta campos requeridos',
          })
          setLoading(false)
        } else {
          await updateCredit(credit!.code, {
            step: CreditSteps.Phase1Authorized,
          }).then(async () => {
            const documents: FileWithBuffer[] = []
            if (analysisResultBuffer.length > 0) {
              documents.push(...analysisResultFile)
            }
            const pushedCreditDocuments = await uploadDocuments(
              documents,
              `credit/${credit!.code.toString()}`
            )

            pushedCreditDocuments.forEach((document) => {
              addCreditDocument(credit.code, document!)
            })
            const comment = `Credito Condicionado :: ${commentRef.current?.value}`
            const creditHistoryComment = {
              documents: pushedCreditDocuments,
              comment: comment,
              type: 'status',
              start: new Date(),
              change: {
                from: {
                  owner: credit.owners.phase1Operator._id,
                  phase: credit.phase,
                  step: credit.step,
                },
                to: {
                  owner: credit.owners.advisor._id,
                  phase: CreditPhases.Phase1,
                  step: CreditSteps.Phase1Authorized,
                },
              },
            }

            await addCreditHistory(credit!.code, creditHistoryComment).then(
              () => {
                if (commentRef.current) {
                  commentRef.current.value = ''
                }
                refetch().then(() => {
                  setLoading(false)
                })
              }
            )
          })
        }
      }

      const handleSendToRejected = async () => {
        setLoading(true)

        const hasComment = !!commentRef.current?.value
        if (!hasComment) {
          setAlert!({
            severity: 'error',
            title: 'Error',
            description: 'Hacen falta campos requeridos',
          })
          setLoading(false)
        } else {
          await updateCredit(credit!.code, {
            step: CreditSteps.Phase1Rejected,
          }).then(async () => {
            const documents: FileWithBuffer[] = []
            if (analysisResultBuffer.length > 0) {
              documents.push(...analysisResultFile)
            }
            const pushedCreditDocuments = await uploadDocuments(
              documents,
              `credit/${credit!.code.toString()}`
            )

            pushedCreditDocuments.forEach((document) => {
              addCreditDocument(credit.code, document!)
            })
            const comment = `Credito Rechazado :: ${commentRef.current?.value}`
            const creditHistoryComment = {
              documents: pushedCreditDocuments,
              comment: comment,
              type: 'status',
              start: new Date(),
              change: {
                from: {
                  owner: credit.owners.phase1Operator._id,
                  phase: credit.phase,
                  step: credit.step,
                },
                to: {
                  owner: credit.owners.advisor._id,
                  phase: CreditPhases.Phase1,
                  step: CreditSteps.Phase1Rejected,
                },
              },
            }

            await addCreditHistory(credit!.code, creditHistoryComment).then(
              () => {
                if (commentRef.current) {
                  commentRef.current.value = ''
                }
                refetch().then(() => {
                  setLoading(false)
                })
              }
            )
          })
        }
      }

      const handleSentToAuthorized = async () => {
        setLoading(true)

        const hasComment = !!commentRef.current?.value
        const hasAnalysisResultFile = analysisResultBuffer.length > 0
        if (!hasComment || !hasAnalysisResultFile) {
          setAlert!({
            severity: 'error',
            title: 'Error',
            description: 'Hacen falta campos requeridos',
          })
          setLoading(false)
        } else {
          await updateCredit(credit!.code, {
            step: CreditSteps.Phase1Authorized,
          }).then(async () => {
            const documents: FileWithBuffer[] = []
            if (analysisResultBuffer.length > 0) {
              documents.push(...analysisResultFile)
            }
            const pushedCreditDocuments = await uploadDocuments(
              documents,
              `credit/${credit!.code.toString()}`
            )

            pushedCreditDocuments.forEach((document) => {
              addCreditDocument(credit.code, document!)
            })
            const comment = `Credito Autorizado :: ${commentRef.current?.value}`
            const creditHistoryComment = {
              documents: pushedCreditDocuments,
              comment: comment,
              type: 'status',
              start: new Date(),
              change: {
                from: {
                  owner: credit.owners.phase1Operator._id,
                  phase: credit.phase,
                  step: credit.step,
                },
                to: {
                  owner: credit.owners.advisor._id,
                  phase: CreditPhases.Phase1,
                  step: CreditSteps.Phase1Authorized,
                },
              },
            }

            await addCreditHistory(credit!.code, creditHistoryComment).then(
              () => {
                if (commentRef.current) {
                  commentRef.current.value = ''
                }
                refetch().then(() => {
                  setLoading(false)
                })
              }
            )
          })
        }
      }

      const handleSendBack = async () => {
        setLoading(true)

        await updateCredit(credit!.code, {
          step: CreditSteps.Phase1Assignation,
        }).then(async () => {
          const comment = `Regresando a Etapa 1 - Asignación Analisis :: ${commentRef.current?.value}`
          const creditHistoryComment = {
            comment: comment,
            type: 'status',
            start: new Date(),
            change: {
              from: {
                phase: credit.phase,
                step: credit.step,
              },
              to: {
                phase: CreditPhases.Phase1,
                step: CreditSteps.Phase1Assignation,
              },
            },
          }

          await addCreditHistory(credit!.code, creditHistoryComment).then(
            () => {
              if (commentRef.current) {
                commentRef.current.value = ''
              }
              refetch().then(() => {
                setLoading(false)
              })
            }
          )
        })
      }

      content = (
        <Fragment>
          <StyledDescription>
            Una vez que se tenga respuesta del banco, favor de reportar el
            estatus del expediente y respaldar pantalla o correo de Banco.
          </StyledDescription>
          <StyledDescription>
            En caso de Inconsistencia favor de resolver, si sale de tu alcance
            derivar a asesora.
          </StyledDescription>
          <FileDropper
            label="Resultado Analisis"
            onChange={setAnalysisResultFile}
            files={analysisResultBuffer}
          />
          <Textarea label="Comentarios" ref={commentRef} required />
        </Fragment>
      )

      footer = (
        <Container align="right" direction="row" padding>
          {loading ? (
            <Spinner type="bar" />
          ) : (
            <Fragment>
              {user?.role !== Role.Advisor && (
                <Button
                  color={color}
                  priority="secondary"
                  onClick={handleSendBack}
                  loading={loading}
                >
                  Regresar a Asignación Analisis
                </Button>
              )}
              <Button
                color={color}
                priority="secondary"
                onClick={handleSendToInconsistency}
              >
                Inconsistencia
              </Button>
              <Button
                color={color}
                priority="secondary"
                onClick={handleSendToConditional}
              >
                Condicionado
              </Button>
              <Button
                color={color}
                priority="secondary"
                onClick={handleSendToRejected}
              >
                Rechazado
              </Button>
              <Button color={color} onClick={handleSentToAuthorized}>
                Autorizado
              </Button>
            </Fragment>
          )}
        </Container>
      )
    } else {
      content = (
        <CreditWaitAction
          owner={credit.owners.phase1Operator}
          text="Tu expediente esta completo y esta en proceso de evaluacion, gracias."
        />
      )
    }
  }

  return (
    <div>
      <Card header={header} footer={footer}>
        {content}
      </Card>
    </div>
  )
}

export default Phase1Analysis
